import {
  Button,
  Card,
  Descriptions,
  Empty,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  getRoadmapCompany,
  advanceRoadmapCompany,
  retreatRoadmapCompany,
  getRoadmapStages,
  getRoadmapRequirements,
  getAnswers,
} from "../../repository/data.repository";
import { RoadmapCompany, RoadmapLevelAction, Answer } from "../../repository/data.default";
import View from "../../controls/View";
import AnswerFormDialog from "../answers/AnswerFormDialog";
import { QueryOperation } from "../../code/search-query/search-query";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";

export interface RoadmapAssignmentManageProps {
  readonly open?: boolean;
  readonly uid?: string | null;
  readonly onClose: () => void;
}

// A roadmap questionnaire to review (its answers are loaded + paginated lazily, per questionnaire).
interface QuestionnaireRef {
  readonly uid: string;
  readonly name: string;
  // Every version of the questionnaire: answers given on an earlier version still belong to this step.
  readonly lineageUid?: string | null;
}

const ANSWERS_PAGE_SIZE = 10;

// Full-page operator surface for a single assignment: control the company's progress and review the
// company's answers to the roadmap's questionnaires (opens the existing answer + KPI-results viewer).
// Progress is ONLY changed via the guarded Step Back / Verify & Advance actions (correct stage
// rollover + room for future side-effects); there is intentionally no free-form pointer editing.
function RoadmapAssignmentManage(props: RoadmapAssignmentManageProps) {
  const permissions = useSelector(authorizeSelectors.permissions);
  const canEdit = permissions.can("ProcessesAssignment", "edit");

  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState<RoadmapCompany | null>(null);
  const [stepping, setStepping] = useState(false);
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireRef[]>([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [answer, setAnswer] = useState<Answer | null>(null);

  // Resolve the roadmap's published AnswerQuestionnaire requirements → the linked questionnaires
  // (their answers are fetched + paginated lazily by each QuestionnaireAnswers below).
  const loadReview = useCallback(async (roadmapUid: string) => {
    setReviewLoading(true);
    try {
      const stagesData = await getRoadmapStages({
        searchQuery: {
          conditions: [{ field: "roadmapUid", values: [roadmapUid], operation: QueryOperation.Equal }],
        },
        pageNumber: 0,
        pageSize: 1000,
        sortBy: "order",
        sortDescending: false,
      });
      const stageUids = stagesData.data.filter((s) => s.isPublished).map((s) => s.uid);

      const reqLists = await Promise.all(
        stageUids.map((stageUid) =>
          getRoadmapRequirements({
            searchQuery: {
              conditions: [
                { field: "roadmapLevelUid", values: [stageUid], operation: QueryOperation.Equal },
              ],
            },
            pageNumber: 0,
            pageSize: 1000,
            sortBy: "order",
            sortDescending: false,
          }),
        ),
      );

      // Unique published questionnaires linked by AnswerQuestionnaire requirements.
      const seen = new Set<string>();
      const questionnaires = reqLists
        .flatMap((r) => r.data)
        .filter(
          (r) =>
            r.isPublished &&
            r.availableActions === RoadmapLevelAction.AnswerQuestionnaire &&
            !!r.assignableQuestionnaireUid &&
            !seen.has(r.assignableQuestionnaireUid) &&
            seen.add(r.assignableQuestionnaireUid),
        )
        .map((r) => ({
          uid: r.assignableQuestionnaireUid!,
          name: r.assignableQuestionnaireName ?? "",
          lineageUid: r.assignableQuestionnaireLineageUid,
        }));

      setQuestionnaires(questionnaires);
    } catch {
      setQuestionnaires([]);
    } finally {
      setReviewLoading(false);
    }
  }, []);

  const load = useCallback(
    (uid: string) => {
      setLoading(true);
      return getRoadmapCompany(uid)
        .then((it) => {
          setItem(it);
          if (it.roadmapUid) loadReview(it.roadmapUid);
        })
        .catch(() => setItem(null))
        .finally(() => setLoading(false));
    },
    [loadReview],
  );

  useEffect(() => {
    if (props.open && props.uid) {
      load(props.uid);
    } else {
      setItem(null);
      setQuestionnaires([]);
    }
  }, [props.open, props.uid, load]);

  // Re-fetch the assignment after a progress step (keeps rowVersion fresh).
  function reloadItem(): void {
    if (!props.uid) return;
    getRoadmapCompany(props.uid).then(setItem);
  }

  function onStep(direction: "advance" | "retreat"): void {
    if (!item?.uid || stepping) return;
    setStepping(true);
    const step = direction === "advance" ? advanceRoadmapCompany : retreatRoadmapCompany;
    step(item.uid)
      .then(() => reloadItem())
      .catch(() => message.error("Couldn't update progress — please retry."))
      .finally(() => setStepping(false));
  }

  return (
    <View
      open={props.open}
      loading={loading}
      title={item ? `Manage Assignment — ${item.companyName}` : "Manage Assignment"}
      onBack={props.onClose}
    >
      <div className="flex flex-col gap-3 p-2">
        <Card styles={{ body: { padding: 16 } }}>
          <div className="flex flex-col">
            <Typography.Title level={4} className="!mb-0">
              {item?.companyName}
            </Typography.Title>
            <Typography.Text type="secondary">{item?.roadmapName}</Typography.Text>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Descriptions
                  title="Progress"
                  bordered
                  size="small"
                  column={1}
                  items={[
                    {
                      key: "stage",
                      label: "Current Stage",
                      children: <Tag color="blue">{item?.currentStage ?? 1}</Tag>,
                    },
                    {
                      key: "requirement",
                      label: "Current Requirement",
                      children: <Tag color="blue">{item?.currentRequirement ?? 1}</Tag>,
                    },
                  ]}
                />
                {canEdit && (
                  <Space>
                    <Popconfirm
                      title="Step this company back one step?"
                      okText="Step Back"
                      onConfirm={() => onStep("retreat")}
                    >
                      <Button icon={<ArrowLeftOutlined />} loading={stepping}>
                        Step Back
                      </Button>
                    </Popconfirm>
                    <Popconfirm
                      title="Mark this requirement complete and unlock the next step for the company?"
                      okText="Advance"
                      okButtonProps={{ type: "primary" }}
                      onConfirm={() => onStep("advance")}
                    >
                      <Button type="primary" loading={stepping}>
                        Verify &amp; Advance <ArrowRightOutlined />
                      </Button>
                    </Popconfirm>
                  </Space>
                )}
              </div>
              <Descriptions
                title="Audit"
                bordered
                size="small"
                column={1}
                items={[
                  {
                    key: "createdOn",
                    label: "Assigned On",
                    children: item?.createdOn
                      ? dayjs(item.createdOn).format("DD/MM/YYYY HH:mm")
                      : undefined,
                  },
                  {
                    key: "updatedOn",
                    label: "Updated On",
                    children: item?.updatedOn
                      ? dayjs(item.updatedOn).format("DD/MM/YYYY HH:mm")
                      : undefined,
                  },
                ]}
              />
          </div>
        </Card>

        <Card title="Assessment Review" styles={{ body: { padding: 16 } }}>
          {reviewLoading ? (
            <div className="flex justify-center p-6">
              <Spin />
            </div>
          ) : questionnaires.length === 0 ? (
            <Empty description="No assessment questionnaires on this roadmap yet" />
          ) : (
            <div className="flex flex-col gap-6">
              {questionnaires.map((q) => (
                <QuestionnaireAnswers
                  key={q.uid}
                  companyUid={item?.companyUid ?? ""}
                  questionnaireUid={q.uid}
                  questionnaireLineageUid={q.lineageUid}
                  questionnaireName={q.name}
                  onOpen={setAnswer}
                />
              ))}
            </div>
          )}
        </Card>
      </div>

      {answer && (
        <AnswerFormDialog
          open
          initialValues={answer}
          mode="view"
          onCancel={() => setAnswer(null)}
        />
      )}
    </View>
  );
}

// Server-paginated answers for one questionnaire (a company can have thousands of respondents for
// employee-distributed assessments, so we page rather than load all at once).
interface QuestionnaireAnswersProps {
  readonly companyUid: string;
  readonly questionnaireUid: string;
  readonly questionnaireLineageUid?: string | null;
  readonly questionnaireName: string;
  readonly onOpen: (answer: Answer) => void;
}

function QuestionnaireAnswers(props: QuestionnaireAnswersProps) {
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchPage = useCallback(
    (p: number) => {
      if (!props.companyUid) return;
      setLoading(true);
      getAnswers({
        searchQuery: {
          conditions: [
            { field: "companyUid", values: [props.companyUid], operation: QueryOperation.Equal },
            // By lineage when known, so answers given on an earlier version of the step's questionnaire stay listed.
            props.questionnaireLineageUid
              ? { field: "questionnaireLineageUid", values: [props.questionnaireLineageUid], operation: QueryOperation.Equal }
              : { field: "questionnaireUid", values: [props.questionnaireUid], operation: QueryOperation.Equal },
          ],
        },
        pageNumber: p - 1,
        pageSize: ANSWERS_PAGE_SIZE,
        sortBy: "createdOn",
        sortDescending: true,
      })
        .then((d) => {
          setAnswers(d.data);
          setTotal(d.totalRecords);
          setPage(p);
        })
        .catch(() => {
          setAnswers([]);
          setTotal(0);
        })
        .finally(() => setLoading(false));
    },
    [props.companyUid, props.questionnaireUid, props.questionnaireLineageUid],
  );

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  return (
    <div className="flex flex-col gap-1">
      <Space>
        <Typography.Text strong>{props.questionnaireName}</Typography.Text>
        <Tag color="blue">{total}</Tag>
      </Space>
      <Table
        size="small"
        rowKey="uid"
        loading={loading}
        dataSource={answers}
        locale={{ emptyText: "Not yet answered" }}
        columns={[
          { title: "Respondent", dataIndex: "userUserName", key: "userUserName", ellipsis: true },
          {
            title: "Status",
            key: "status",
            width: 120,
            render: (_: any, a: Answer) => (
              <Tag color={a.completed ? "green" : "orange"}>{a.completed ? "Completed" : "Draft"}</Tag>
            ),
          },
          {
            title: "Scored",
            key: "scored",
            width: 120,
            render: (_: any, a: Answer) => (
              <Tag color={a.scoringCompleted ? "blue" : "default"}>
                {a.scoringCompleted ? "Scored" : "Not scored"}
              </Tag>
            ),
          },
          {
            title: "Submitted",
            key: "createdOn",
            width: 160,
            render: (_: any, a: Answer) =>
              a.createdOn ? dayjs(a.createdOn).format("DD/MM/YYYY HH:mm") : "",
          },
          {
            title: "",
            key: "open",
            width: 80,
            align: "right" as const,
            render: (_: any, a: Answer) => (
              <Button size="small" type="link" onClick={() => props.onOpen(a)}>
                Open
              </Button>
            ),
          },
        ]}
        pagination={{
          current: page,
          pageSize: ANSWERS_PAGE_SIZE,
          total,
          showSizeChanger: false,
          hideOnSinglePage: true,
          onChange: (p) => fetchPage(p),
        }}
      />
    </div>
  );
}

export default RoadmapAssignmentManage;
